import {
  computeConstellationScenePositions,
  type ConstellationEdgeEntry,
  type ConstellationSceneEntry,
} from "@chuzi/shared/realms/cosmos/components";
import { cosmosMapping } from "@chuzi/shared/realms/cosmos";
import type {
  SceneListItem,
  SceneMapEntry,
  StoryListItem,
  TreeGraph,
} from "@chuzi/shared/types";
import { isSystemStory } from "@chuzi/shared/types";
import type { ReactNode } from "react";
import { editorVisibleScenes } from "./story-scaffold";
import { orderScenesForConstellation, resolveSceneStarHue } from "./constellation-layout";

export type CatalogConstellationGroup = {
  story: StoryListItem;
  storyIndex: number;
  anchor: [number, number, number];
  scenes: ConstellationSceneEntry[];
  edges: ConstellationEdgeEntry[];
};

export function buildCatalogConstellationGroups(input: {
  stories: StoryListItem[];
  storyPositions: Map<string, [number, number, number]>;
  scenes: Record<string, SceneListItem[]>;
  storyGraphs: Record<string, TreeGraph>;
  sceneMaps: Record<string, Record<string, SceneMapEntry>>;
  coverboxes: Record<string, string | null>;
  focusedIndex: number;
  /** When false, stars render without preview cards (label-only browse). */
  includePreviews?: boolean;
  buildPreview?: (args: {
    scene: SceneListItem;
    story: StoryListItem;
    mapEntry: SceneMapEntry | null;
    coverboxUrl: string | null;
    focused: boolean;
  }) => ReactNode;
}): CatalogConstellationGroup[] {
  const {
    stories,
    storyPositions,
    scenes,
    storyGraphs,
    sceneMaps,
    coverboxes,
    focusedIndex,
    includePreviews = true,
    buildPreview,
  } = input;

  return stories.map((story, storyIndex) => {
    const anchor = storyPositions.get(story.id) ?? [0, 0, 0];
    const storyScenes = scenes[story.id];
    const graph = storyGraphs[story.id];
    const isStoryFocused = storyIndex === focusedIndex;
    const baseVisual = cosmosMapping(story);
    const coverboxUrl = coverboxes[story.id] ?? null;

    if (!storyScenes?.length) {
      const placeholderScene: SceneListItem = {
        id: `${story.id}-title`,
        story_id: story.id,
        title: story.title,
        order: 0,
        is_title: true,
        is_end: false,
        color: null,
        media_id: null,
        alt_media_id: null,
        media_mode: null,
        text_content: null,
        text_style: null,
        created_at: story.created_at,
        updated_at: story.updated_at,
      };

      return {
        story,
        storyIndex,
        anchor,
        edges: [],
        scenes: [
          {
            id: story.id,
            isTitle: true,
            isEnd: false,
            visual: { ...baseVisual, position: anchor },
            label: story.title,
            previewContent:
              includePreviews && buildPreview
                ? buildPreview({
                    scene: placeholderScene,
                    story,
                    mapEntry: null,
                    coverboxUrl,
                    focused: isStoryFocused,
                  })
                : undefined,
            dimmed: !isStoryFocused,
            locked: false,
            focused: isStoryFocused,
          },
        ],
      };
    }

    const displayScenes = orderScenesForConstellation(
      editorVisibleScenes(storyScenes),
      graph,
    );
    const scenePositions = computeConstellationScenePositions(
      displayScenes,
      graph,
      anchor,
    );

    const constellationScenes: ConstellationSceneEntry[] = displayScenes.map(
      (scene) => {
        const basePosition = scenePositions.get(scene.id) ?? anchor;
        const savedOffset = isSystemStory(story) ? null : scene.layout_offset;
        const position: [number, number, number] = [
          basePosition[0] + (savedOffset?.x ?? 0),
          basePosition[1] + (savedOffset?.y ?? 0),
          basePosition[2] + (savedOffset?.z ?? 0),
        ];
        const mapEntry = sceneMaps[story.id]?.[scene.id] ?? null;
        const hue = resolveSceneStarHue(scene, baseVisual.hue);
        const scaleMultiplier = scene.is_title ? 1.15 : 1;

        return {
          id: scene.id,
          isTitle: scene.is_title,
          isEnd: scene.is_end,
          visual: {
            ...baseVisual,
            position,
            hue,
            scale: baseVisual.scale * scaleMultiplier,
          },
          label: scene.is_title ? story.title : scene.title,
          previewContent:
            includePreviews && buildPreview
              ? buildPreview({
                  scene,
                  story,
                  mapEntry,
                  coverboxUrl,
                  focused: isStoryFocused && scene.is_title,
                })
              : undefined,
          dimmed: !isStoryFocused,
          locked: !scene.is_title,
          focused: isStoryFocused && scene.is_title,
        };
      },
    );

    const edges: ConstellationEdgeEntry[] = (graph?.edges ?? []).map((edge) => ({
      source: edge.source,
      target: edge.target,
      type: edge.type,
    }));

    return {
      story,
      storyIndex,
      anchor,
      scenes: constellationScenes,
      edges,
    };
  });
}
